/**
 * Shared JMAP transport — used by every sub-app's `jmapRequest` helper.
 *
 * Each app declares which mux channel name corresponds to its backend
 * module (`mail`, `calendar`, …) and the transport then routes method calls
 * based on a per-channel mode setting:
 *
 *   - `'auto'` (default) — prefer WS when the shared socket is ready,
 *                          fall back to HTTP transparently otherwise.
 *   - `'ws'`             — send a `Request` frame over the master mux
 *                          and await the matching `Response` (RFC 8887).
 *   - `'http'`           — POST the envelope to the JMAP HTTP endpoint.
 *
 * The mode is read at call-time from localStorage:
 *
 *   `formica.jmap.transport.<channel>` → 'http' | 'ws' | 'auto'
 *
 * so a switch in the UI (or a quick DevTools tweak) takes effect on the
 * next call without re-instantiating the transport.
 */
import NxSocketClient from '../socket/websocket';
import { readBearer } from '../auth/fetch';
import { recoverSocketAuthentication } from '../socket/authentication';

export type JmapMethodCall = unknown[];

export interface JmapRequest {
  using: string[];
  methodCalls: JmapMethodCall[];
  createdIds?: Record<string, string>;
}

export interface JmapResponse {
  sessionState: string;
  methodResponses: JmapMethodCall[];
  createdIds?: Record<string, string>;
}

export type JmapTransportMode = 'http' | 'ws' | 'auto';

export interface JmapTransportConfig {
  /** Master mux channel name backing this transport ('mail', 'calendar', …). */
  channel: string;
  /**
   * Runs the request over HTTP. The caller owns auth (bearer + refresh),
   * module routing headers, retry logic, etc. — keeps the transport
   * independent of any app's specific HTTP wrapper. When using
   * `pickAndRun()`, the transport delegates here for the HTTP path.
   * Optional: callers that prefer to branch inline in their own
   * `jmapRequest` can leave this undefined and use `requestOverWs` /
   * `shouldUseWs` directly.
   */
  httpRequest?: (req: JmapRequest) => Promise<JmapResponse>;
  /**
   * Shared mux client. Can be a getter so it lazy-resolves the singleton
   * after Connect's shell has set it.
   */
  socket?: NxSocketClient | null | (() => NxSocketClient | null);
  /**
   * Mode override. When omitted (the normal case) the transport reads
   * `formica.jmap.transport.<channel>` from localStorage on every call.
   */
  mode?: JmapTransportMode;
  /** WS request timeout. Defaults to 30 s. */
  wsTimeoutMs?: number;
}

const STORAGE_KEY_PREFIX = 'formica.jmap.transport.';
const DEFAULT_MODE: JmapTransportMode = 'auto';
const memoryModes = new Map<string, JmapTransportMode>();

function getTransportStorage(): Storage | null {
  try {
    return globalThis.localStorage ?? null;
  } catch {
    return null;
  }
}

function readStorageMode(storage: Storage | null, key: string): JmapTransportMode | null {
  try {
    const raw = storage?.getItem(key) || '';
    return raw === 'http' || raw === 'ws' || raw === 'auto' ? raw : null;
  } catch {
    return null;
  }
}

function persistStorageMode(storage: Storage | null, key: string, mode: JmapTransportMode): void {
  try {
    storage?.setItem(key, mode);
  } catch {
    // Runtime memory mode still keeps the current session switchable.
  }
}

/** Reads the persisted transport mode for a channel; defaults to `'auto'`. */
export function getJmapTransportMode(channel: string): JmapTransportMode {
  const storage = getTransportStorage();
  const mode = readStorageMode(storage, STORAGE_KEY_PREFIX + channel);
  if (mode) return mode;
  return memoryModes.get(channel) ?? DEFAULT_MODE;
}

/** Persists the transport mode for a channel. */
export function setJmapTransportMode(channel: string, mode: JmapTransportMode): void {
  memoryModes.set(channel, mode);
  const storage = getTransportStorage();
  persistStorageMode(storage, STORAGE_KEY_PREFIX + channel, mode);
}

interface PendingWsRequest {
  resolve: (r: JmapResponse) => void;
  reject: (e: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

interface JmapWSResponseEnvelope {
  '@type'?: string;
  requestId?: string;
  sessionState?: string;
  methodResponses?: unknown[];
  createdIds?: Record<string, string>;
  status?: number;
  type?: string;
  detail?: string;
}

class JmapWsRequestError extends Error {
  constructor(readonly status: number | undefined, detail: string) {
    super(`JMAP WS RequestError ${status || ''}: ${detail}`);
    this.name = 'JmapWsRequestError';
  }
}

function isReadOnly(req: JmapRequest): boolean {
  return req.methodCalls.every(([method]) => typeof method === 'string'
    && (/\/(get|query|changes|queryChanges|parse|validate)$/.test(method) || method === 'Core/echo'));
}

export class JmapTransport {
  private pending = new Map<string, PendingWsRequest>();
  private wsListenerActive = false;
  private wsDisconnectUnsub: (() => void) | null = null;
  private nextId = 0;

  constructor(private readonly config: JmapTransportConfig) {}

  /** Effective mode — config override wins over persisted setting. */
  get mode(): JmapTransportMode {
    return this.config.mode || getJmapTransportMode(this.config.channel);
  }

  /** Lazily resolves the socket (singleton may not exist at construction). */
  private getSocket(): NxSocketClient | null {
    const s = this.config.socket;
    if (typeof s === 'function') return s();
    return s ?? null;
  }

  /** Returns true if the WS path is available right now. */
  isWsReady(): boolean {
    const s = this.getSocket();
    return !!(s && s.isReady());
  }

  /**
   * Decides whether to use WS based on the current mode + socket state.
   * Callers that branch inline use this to pick a transport.
   */
  shouldUseWs(): boolean {
    const m = this.mode;
    if (m === 'http') return false;
    if (m === 'ws') return true;
    // 'auto'
    return this.isWsReady();
  }

  /**
   * One-shot helper that picks transport and runs the request.
   * Requires `config.httpRequest` for the HTTP path. Callers with custom
   * HTTP wiring (auth retry, etc.) usually branch inline and call
   * `requestOverWs` directly.
   */
  async request(req: JmapRequest, overrideMode?: JmapTransportMode): Promise<JmapResponse> {
    const mode = overrideMode || this.mode;
    if (mode === 'ws' || mode === 'auto') {
      if (this.isWsReady()) {
        try {
          return await this.requestOverWs(req);
        } catch (err) {
          if (mode === 'ws' || !isReadOnly(req)) throw err;
          if (typeof console !== 'undefined') {
            console.warn(`[jmap/${this.config.channel}] WS request failed, falling back to HTTP:`, err);
          }
        }
      } else if (mode === 'ws') {
        throw new Error(
          `JMAP transport: WebSocket not ready for channel '${this.config.channel}'`,
        );
      }
    }
    if (!this.config.httpRequest) {
      throw new Error(
        `JMAP transport: HTTP path not configured for channel '${this.config.channel}'`,
      );
    }
    return this.config.httpRequest(req);
  }

  /** Sends one JMAP request over the master mux and awaits the response. */
  async requestOverWs(req: JmapRequest): Promise<JmapResponse> {
    const socket = this.getSocket();
    if (!socket) {
      throw new Error(`JMAP transport: no shared socket for channel '${this.config.channel}'`);
    }
    const bearer = readBearer();
    try {
      return await this.sendRequestOverWs(socket, req);
    } catch (err) {
      // RequestError rejects the whole envelope before method execution, so
      // a 401 is safe to retry even for writes. Never replay an ambiguous
      // timeout/disconnect: the original write may already have committed.
      if (!(err instanceof JmapWsRequestError) || err.status !== 401) throw err;
      if (!await recoverSocketAuthentication(socket, bearer, this.config.wsTimeoutMs ?? 30_000)) throw err;
      return this.sendRequestOverWs(socket, req);
    }
  }

  private sendRequestOverWs(socket: NxSocketClient, req: JmapRequest): Promise<JmapResponse> {
    if (!socket.isReady()) {
      // Do not queue a write that could be sent later, after its caller times out.
      return Promise.reject(new Error(`JMAP WebSocket not ready for channel '${this.config.channel}'`));
    }
    const id = `r-${++this.nextId}-${Date.now()}`;
    const ch = socket.channel(this.config.channel);

    if (!this.wsListenerActive) {
      ch.on('data', (raw: unknown) => this.handleWsFrame(raw));
      ch.on('event', (env: { evt?: string; code?: number; reason?: string }) => {
        if (env.evt === 'error') {
          // Failed upstream admission: none of these requests reached JMAP.
          this.failAllPending(new JmapWsRequestError(env.code, env.reason || 'channel unavailable'));
        } else if (env.evt === 'close') {
          this.failAllPending();
        }
      });
      this.wsListenerActive = true;
    }
    if (!this.wsDisconnectUnsub) {
      this.wsDisconnectUnsub = socket.on('disconnected', () => this.failAllPending());
    }

    const timeoutMs = this.config.wsTimeoutMs ?? 30_000;
    return new Promise<JmapResponse>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pending.delete(id);
        reject(new Error(`JMAP WS request ${id} timed out after ${timeoutMs}ms`));
      }, timeoutMs);
      this.pending.set(id, { resolve, reject, timer });
      try {
        ch.send({
          '@type': 'Request',
          id,
          using: req.using,
          methodCalls: req.methodCalls,
          createdIds: req.createdIds,
        });
      } catch (err) {
        clearTimeout(timer);
        this.pending.delete(id);
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    });
  }

  private handleWsFrame(raw: unknown): void {
    const env = (raw ?? {}) as JmapWSResponseEnvelope;
    const kind = env['@type'];
    if (kind === 'Response' && env.requestId) {
      const p = this.pending.get(env.requestId);
      if (!p) return;
      clearTimeout(p.timer);
      this.pending.delete(env.requestId);
      p.resolve({
        sessionState: env.sessionState || '',
        methodResponses: (env.methodResponses || []) as JmapMethodCall[],
        createdIds: env.createdIds,
      });
    } else if (kind === 'RequestError' && env.requestId) {
      const p = this.pending.get(env.requestId);
      if (!p) return;
      clearTimeout(p.timer);
      this.pending.delete(env.requestId);
      p.reject(
        new JmapWsRequestError(env.status, env.detail || env.type || 'unknown'),
      );
    }
    // `StateChange` and other push envelopes are handled by
    // `useJmapWebSocket` — not the transport's concern.
  }

  private failAllPending(error?: Error): void {
    if (this.pending.size === 0) return;
    const err = error ?? new Error(
      `JMAP WS for '${this.config.channel}' disconnected — pending requests cancelled`,
    );
    for (const [id, p] of this.pending) {
      clearTimeout(p.timer);
      p.reject(err);
      this.pending.delete(id);
    }
  }
}
